import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useHasSupportedVideoCodec } from '../../hooks/useHasSupportedVideoCodec';
import { useTrackBroadcastDurationEffect } from '../../hooks/useTrackBroadcastDurationEffect';
import { useWakeLock } from '../../hooks/useWakeLock';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { LobbyService } from '../../services/lobby/LobbyService';
import { MediaService } from '../../services/media/MediaService';
import { SceneService } from '../../services/scene/SceneService';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { UserService } from '../../services/user/UserService';
import { AcceptedParticipantType, UserRole, } from '../../services/user/UserServiceOutput';
import { useTimedFlag } from '../../utils/time';
import { formBroadcastUrl } from '../../utils/url';
import { Button } from '../Button';
import { FullscreenButton } from '../FullscreenButton';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotSupported, FirefoxNotSupported } from '../NotSupportedMessage';
import { Stage } from '../Stage';
import { PluginComponentContainer } from '../shared/PluginComponentContainer';
import { PluginOverlayContainer } from '../shared/PluginOverlayContainer';
import { VideoDisplay } from '../VideoDisplay';
import { VideoOverlayDisplay } from '../VideoOverlayDisplay';
import { SlowConnectionNotice } from '../viewer/SlowConnectionNotice';
import { MobileView } from '../views/MobileView';
import { BlockedPermissions } from '../views/View';
import { AudioUnmuteOverlay } from './AudioUnmuteOverlay';
import { DisplayOutput } from './DisplayOutput';
import { Lobby } from './Lobby';
import { ParticipantButtonOverlay } from './ParticipantButtonOverlay';
import { StreamrAppGuestViewMobileHud } from './StreamrAppGuestViewMobileHud';
import { StreamrAppSpeakerViewMobileHud } from './StreamrAppSpeakerViewMobileHud';
import { StreamrAppViewerViewMobileHud } from './StreamrAppViewerViewMobileHud';
export function StreamrAppMobileView() {
    const navigate = useNavigate();
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const userRole = UserService.getOutput().useUserRole();
    const isViewerRole = userRole === UserRole.VIEWER;
    const isGuestRole = userRole === UserRole.GUEST;
    const showsViewerStream = isViewerRole || isGuestRole;
    const streamrAppService = StreamrAppService.getNotificationInterface();
    const displayScene = SceneService.getOutput().useDisplayScene();
    const broadcastOutputBroadcasting = StreamrAppService.getOutput().useIsVideoStreaming();
    const contentQuality = StreamrAppService.getOutput().useContentQuality();
    // the slow-connection notice / retry caption replaces the pre-live message
    const contentNoticeShown = contentQuality?.failure !== undefined ||
        contentQuality?.recovering === true;
    const broadcastOutputReady = StreamrAppService.getOutput().useReady();
    const sessionMode = StreamrAppService.getOutput().useSessionMode();
    const acceptedParticipantType = UserService.getOutput().useAcceptedParticipantType();
    const [goLivePending, setGoLivePending] = useState(false);
    const isBroadcaster = activeBroadcast.myRole === 'broadcaster';
    const lobbyView = LobbyService.getOutput().useLobbyView();
    const prevShowsViewerStreamRef = useRef(showsViewerStream);
    const prevIsGuestRoleRef = useRef(isGuestRole);
    useEffect(() => {
        if (prevShowsViewerStreamRef.current &&
            !showsViewerStream &&
            !isBroadcaster) {
            LobbyService.getInstance().notifyPromotedFromViewer();
        }
        if (!prevIsGuestRoleRef.current && isGuestRole) {
            LobbyService.getInstance().notifyPromotedFromViewer();
        }
        prevShowsViewerStreamRef.current = showsViewerStream;
        prevIsGuestRoleRef.current = isGuestRole;
    }, [showsViewerStream, isGuestRole, isBroadcaster]);
    const [isHudVisible, showHud] = useTimedFlag(true, 30000);
    const incomingRequests = UserService.getOutput().useIncomingRequests();
    const prevIncomingCountRef = useRef(incomingRequests.length);
    useEffect(() => {
        if (incomingRequests.length > prevIncomingCountRef.current) {
            showHud();
        }
        prevIncomingCountRef.current = incomingRequests.length;
    }, [incomingRequests.length, showHud]);
    const { requestWakeLock } = useWakeLock({ enabled: true });
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const [viewElement, setViewElement] = useState(null);
    const broadcastUrl = formBroadcastUrl(activeBroadcast);
    const mediaService = MediaService.getNotificationInterface();
    const mediaOutputMicrophonePermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const mediaOutputCameraPermissioned = MediaService.getOutput().useCameraPermissioned();
    const mediaOutputMediaStreamData = MediaService.getOutput().useMediaStreamData();
    useTrackBroadcastDurationEffect({
        enabled: showsViewerStream && broadcastOutputBroadcasting,
        action: 'Watch',
    });
    const handleCanvasMount = useCallback((canvas) => {
        if (canvas) {
            streamrAppService.notifyCanvasMounted(canvas);
        }
    }, [streamrAppService]);
    // --- Speaker/host-specific effects ---
    const needsCapture = !isViewerRole;
    useEffect(() => {
        if (needsCapture) {
            mediaService.notifyStartCaptureRequested();
            return () => {
                mediaService.notifyStopCaptureRequested();
            };
        }
    }, [needsCapture, mediaService]);
    useEffect(() => {
        return () => {
            StreamrAppService.getNotificationInterface().notifyDestroyRendering();
        };
    }, []);
    useEffect(() => {
        if (needsCapture &&
            !mediaOutputMicrophonePermissioned &&
            !mediaOutputCameraPermissioned &&
            !mediaOutputMediaStreamData.isSharingScreen) {
            mediaService.notifyMediaPermissionsRequested({
                audio: true,
                video: true,
            });
        }
    }, [
        needsCapture,
        mediaOutputMicrophonePermissioned,
        mediaOutputCameraPermissioned,
        mediaOutputMediaStreamData.isSharingScreen,
        mediaService,
    ]);
    useEffect(() => {
        if (broadcastOutputBroadcasting) {
            setGoLivePending(false);
        }
    }, [broadcastOutputBroadcasting]);
    // --- Derived state ---
    const hasMediaPermissions = mediaOutputMicrophonePermissioned ||
        mediaOutputCameraPermissioned ||
        mediaOutputMediaStreamData.isSharingScreen;
    const showAnyLobby = lobbyView !== 'none';
    // --- Render ---
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    if (showAnyLobby) {
        return _jsx(Lobby, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: showsViewerStream
                    ? !broadcastOutputReady
                    : goLivePending &&
                        (!broadcastOutputReady ||
                            !broadcastOutputBroadcasting) }), _jsx(MobileView, { innerRef: setViewElement, onTouchStart: () => {
                    showHud();
                }, onTouchEnd: () => {
                    void requestWakeLock();
                }, onMouseDown: () => {
                    showHud();
                }, children: isGuestRole ? (_jsxs(_Fragment, { children: [_jsx(Stage, { children: _jsx(VideoDisplay, { fit: "contain", orientToScreen: true, component: _jsx(DisplayOutput, { onCanvasMount: handleCanvasMount }) }) }), _jsxs(VideoOverlayDisplay, { fit: "contain", orientToScreen: true, children: [!broadcastOutputBroadcasting &&
                                    !contentNoticeShown && (_jsx(ViewerPreLiveMessage, { children: "Waiting for stream..." })), _jsx(SlowConnectionNotice, {}), _jsx(AudioUnmuteOverlay, {}), _jsx(StreamrAppGuestViewMobileHud, { broadcastUrl: broadcastUrl, fullscreenButton: _jsx(FullscreenButton, { element: viewElement }), visible: isHudVisible }), _jsx(ParticipantButtonOverlay, { visible: isHudVisible }), _jsx(PluginOverlayContainer, { slotId: "overlay" }), _jsx(PluginComponentContainer, { slotId: "components" })] })] })) : (_jsxs(_Fragment, { children: [_jsxs(Stage, { children: [_jsx(VideoDisplay, { fit: showsViewerStream ? 'contain' : 'fill', orientToScreen: showsViewerStream || undefined, component: _jsx(DisplayOutput, { onCanvasMount: handleCanvasMount }) }), !showsViewerStream && !hasMediaPermissions && (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: () => {
                                            mediaService.notifyMediaPermissionsRequested({
                                                audio: true,
                                                video: true,
                                            });
                                        }, children: "Allow microphone and\u00A0camera" }) }))] }), showsViewerStream ? (_jsxs(VideoOverlayDisplay, { fit: "contain", orientToScreen: true, children: [!broadcastOutputBroadcasting &&
                                    !contentNoticeShown && (_jsx(ViewerPreLiveMessage, { children: "Waiting for stream..." })), _jsx(SlowConnectionNotice, {}), _jsx(AudioUnmuteOverlay, {}), _jsx(StreamrAppViewerViewMobileHud, { broadcastUrl: broadcastUrl, fullscreenButton: _jsx(FullscreenButton, { element: viewElement }), visible: isHudVisible }), _jsx(ParticipantButtonOverlay, { visible: isHudVisible }), _jsx(PluginOverlayContainer, { slotId: "overlay" }), _jsx(PluginComponentContainer, { slotId: "components" })] })) : (_jsxs(_Fragment, { children: [_jsxs(VideoOverlayDisplay, { fit: "fill", children: [_jsx(AudioUnmuteOverlay, {}), _jsx(StreamrAppSpeakerViewMobileHud, { activeBroadcast: activeBroadcast, broadcastUrl: broadcastUrl, fullscreenButton: _jsx(FullscreenButton, { element: viewElement }), visible: isHudVisible, isBroadcaster: isBroadcaster, isBroadcasting: broadcastOutputBroadcasting, onGoLiveClick: () => {
                                                if (displayScene) {
                                                    SceneService.getNotificationInterface().notifySetLiveScene(displayScene.id);
                                                }
                                                setGoLivePending(true);
                                                streamrAppService.notifyActivateBroadcast(activeBroadcast);
                                            }, onEndBroadcastClick: () => {
                                                const isSessionHost = activeBroadcast.myRole ===
                                                    'broadcaster';
                                                const isCallSession = sessionMode === 'call' ||
                                                    acceptedParticipantType ===
                                                        AcceptedParticipantType.SPEAKERS;
                                                if (isCallSession &&
                                                    !isSessionHost) {
                                                    streamrAppService.notifyDestroyRendering();
                                                    void navigate('/');
                                                    setGoLivePending(false);
                                                    return;
                                                }
                                                streamrAppService.notifyDeactivateBroadcast();
                                                setGoLivePending(false);
                                            } }), _jsx(ParticipantButtonOverlay, { visible: isHudVisible })] }), _jsx(PluginOverlayContainer, { slotId: "overlay" }), _jsx(PluginComponentContainer, { slotId: "components" })] }))] })) })] }));
}
const ViewerPreLiveMessage = styled.div `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 1.5rem;
    text-align: center;
    background: rgba(0, 0, 0, 0.7);
    padding: 2rem 3rem;
    border-radius: 0.5rem;
`;
